/**
 * SMA Data Client — Schwab Primary, Twelve Data Fallback
 *
 * Fetches historical price data to compute SMA 20/50/200 and Parkinson IV.
 * PRIMARY: Schwab /pricehistory via Edge Function (120 req/min, batch-friendly)
 * FALLBACK: Twelve Data /time_series (8 req/min, sequential)
 *
 * Schwab approach: 10 parallel requests at a time, 500ms between batches
 * = ~130 tickers in ~7 seconds (vs 17 minutes with Twelve Data)
 */

import type { SMAData } from './watchlistComputations'
import { supabase } from './supabase'
import { invokeEdgeFunction } from './edgeFunctions'

const TWELVE_DATA_KEY = import.meta.env.VITE_TWELVE_DATA_API_KEY || ''
const TWELVE_DATA_BASE = 'https://api.twelvedata.com'
const CACHE_TTL_MS = 4 * 60 * 60 * 1000 // 4 hours
const SCHWAB_BATCH_SIZE = 10 // parallel requests per batch
const SCHWAB_BATCH_DELAY_MS = 600 // 600ms between batches (stays under 120 req/min)

// In-memory cache
const cache = new Map<string, { data: SMAData; fetchedAt: number }>()

// Queue state
let queue: string[] = []
let processing = false

function computeSMA(closes: number[], period: number): number | null {
  if (closes.length < period) return null
  const slice = closes.slice(0, period)
  const sum = slice.reduce((a, b) => a + b, 0)
  return sum / period
}

function computeParkinsonIV(highs: number[], lows: number[]): number | null {
  const n = Math.min(highs.length, lows.length, 30)
  if (n < 10) return null
  let sumLogSq = 0
  for (let i = 0; i < n; i++) {
    if (lows[i] <= 0) continue
    const logHL = Math.log(highs[i] / lows[i])
    sumLogSq += logHL * logHL
  }
  const sigma = Math.sqrt(sumLogSq / (4 * n * Math.LN2))
  return sigma * Math.sqrt(252)
}

function classifyIVPremium(annualizedVol: number | null): 'Low' | 'Medium' | 'High' | null {
  if (annualizedVol === null) return null
  if (annualizedVol >= 0.50) return 'High'
  if (annualizedVol >= 0.30) return 'Medium'
  return 'Low'
}

/**
 * Fetch historical candles from Schwab via edge function, compute SMAs.
 */
async function fetchViaSchwab(ticker: string): Promise<SMAData | null> {
  try {
    const data = await invokeEdgeFunction<{ candles: { close: number; high: number; low: number; date: string }[]; error?: string }>('schwab-history', {
      params: { symbol: ticker },
    })

    if (data.error || !data.candles || data.candles.length === 0) {
      return null
    }

    // Candles come oldest-first from Schwab — reverse to most-recent-first
    const candles = [...data.candles].reverse()
    const closes = candles.map(c => c.close)
    const highs = candles.map(c => c.high)
    const lows = candles.map(c => c.low)

    const sma_20 = computeSMA(closes, 20)
    const sma_50 = computeSMA(closes, 50)
    const sma_200 = computeSMA(closes, 200)
    const parkinsonIV = computeParkinsonIV(highs, lows)
    const ivPremiumLevel = classifyIVPremium(parkinsonIV)

    return {
      ticker,
      sma_20,
      sma_50,
      sma_200,
      parkinsonIV,
      ivPremiumLevel,
      fetchedAt: Date.now(),
    }
  } catch (err) {
    console.warn(`[SMA/Schwab] ${ticker} failed:`, err)
    return null
  }
}

/**
 * Fallback: Fetch from Twelve Data (sequential, 8s delay).
 */
async function fetchViaTwelveData(ticker: string): Promise<SMAData | null> {
  if (!TWELVE_DATA_KEY) return null
  try {
    const url = `${TWELVE_DATA_BASE}/time_series?symbol=${encodeURIComponent(ticker)}&interval=1day&outputsize=252&apikey=${TWELVE_DATA_KEY}`
    const res = await fetch(url)
    if (!res.ok) return null
    const json = await res.json()
    if (!json.values || json.values.length === 0) return null

    const closes = json.values.map((v: { close: string }) => parseFloat(v.close))
    const highs = json.values.map((v: { high: string }) => parseFloat(v.high))
    const lows = json.values.map((v: { low: string }) => parseFloat(v.low))

    const sma_20 = computeSMA(closes, 20)
    const sma_50 = computeSMA(closes, 50)
    const sma_200 = computeSMA(closes, 200)
    const parkinsonIV = computeParkinsonIV(highs, lows)
    const ivPremiumLevel = classifyIVPremium(parkinsonIV)

    return { ticker, sma_20, sma_50, sma_200, parkinsonIV, ivPremiumLevel, fetchedAt: Date.now() }
  } catch {
    return null
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Persist SMA data to Supabase watchlist row (non-blocking).
 */
function persistToSupabase(result: SMAData): void {
  supabase.from('watchlist').update({
    iv_premium_level: result.ivPremiumLevel,
    sma_20: result.sma_20,
    sma_50: result.sma_50,
    sma_200: result.sma_200,
    sma_updated_at: new Date().toISOString(),
  }).eq('ticker', result.ticker).then(() => {}).catch(() => {})
}

/**
 * Process the queue using Schwab (batch parallel) with Twelve Data fallback.
 */
async function processQueue(onResult: (ticker: string, data: SMAData | null) => void): Promise<void> {
  if (processing) return
  processing = true

  while (queue.length > 0) {
    // Take a batch of tickers
    const batch = queue.splice(0, SCHWAB_BATCH_SIZE)

    // Fetch in parallel
    const results = await Promise.all(
      batch.map(async (ticker) => {
        // Check cache first
        const cached = cache.get(ticker)
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          return { ticker, data: cached.data, fromCache: true }
        }

        // Try Schwab first
        let result = await fetchViaSchwab(ticker)

        // Fallback to Twelve Data if Schwab fails
        if (!result && TWELVE_DATA_KEY) {
          result = await fetchViaTwelveData(ticker)
        }

        if (result) {
          cache.set(ticker, { data: result, fetchedAt: Date.now() })
          persistToSupabase(result)
        }

        return { ticker, data: result, fromCache: false }
      })
    )

    // Report results
    for (const { ticker, data } of results) {
      onResult(ticker, data)
    }

    // Delay between batches (stay under 120 req/min)
    if (queue.length > 0) {
      await delay(SCHWAB_BATCH_DELAY_MS)
    }
  }

  processing = false
}

/**
 * Fetch SMA data for a batch of tickers.
 * Uses Schwab (10 parallel per batch, 600ms between batches).
 */
export function fetchSMABatch(
  tickers: string[],
  onResult: (ticker: string, data: SMAData | null) => void
): void {
  const existing = new Set(queue)
  for (const t of tickers) {
    if (!existing.has(t)) {
      queue.push(t)
    }
  }
  processQueue(onResult)
}

export function getCachedSMA(ticker: string): SMAData | null {
  const cached = cache.get(ticker)
  if (!cached) return null
  if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) return null
  return cached.data
}

export function clearSMACache(): void {
  cache.clear()
}

export function isSMAFetching(): boolean {
  return processing
}
